import { useState, useEffect, useRef, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { useLanguage } from "@/i18n/index";
import ProfessionalLayout from "@/components/layout/ProfessionalLayout";
import MessagingNoticeModal from "@/components/consent/MessagingNoticeModal";
import api from "@/api";
import { toast } from "sonner";
import {
  Send, MessageSquare, User, Plus, X, Stethoscope, Search,
  AlertTriangle, Activity, Clock, ChevronUp, ChevronLeft, Zap, Paperclip, FileText, Download, Image, Trash2
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useWebSocket } from "@/contexts/WebSocketContext";
import { getItems } from "@/utils/apiHelpers";

// Quick reply templates for IDE
const QUICK_REPLIES = [
  "Comment vous sentez-vous aujourd'hui ?",
  "Pensez a bien prendre votre traitement.",
  "N'hesitez pas a nous appeler si vos symptomes s'aggravent.",
  "Votre prochain RDV est bien maintenu.",
  "Merci pour votre check-in, tout est normal.",
  "Le medecin a ete informe, il reviendra vers vous.",
];

// Date separator helper
function getDateLabel(dateStr) {
  const d = new Date(dateStr);
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);

  if (d.toDateString() === today.toDateString()) return "Aujourd'hui";
  if (d.toDateString() === yesterday.toDateString()) return "Hier";
  return d.toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" });
}

function shouldShowDateSeparator(messages, index) {
  if (index === 0) return true;
  const prev = new Date(messages[index - 1].createdAt).toDateString();
  const curr = new Date(messages[index].createdAt).toDateString();
  return prev !== curr;
}

function getMessageFileUrl(file) {
  return file?.url || "";
}

function isImageFile(file) {
  return file?.mimeType?.startsWith("image/");
}

// Status color map
const STATUS_COLORS = {
  CRITICAL: { bg: "bg-rose-50", text: "text-rose-700", border: "border-rose-200", dot: "bg-rose-500" },
  HOSPITALIZED: { bg: "bg-purple-50", text: "text-purple-700", border: "border-purple-200", dot: "bg-purple-500" },
  WATCH: { bg: "bg-amber-50", text: "text-amber-700", border: "border-amber-200", dot: "bg-amber-500" },
  STABLE: { bg: "bg-emerald-50", text: "text-emerald-700", border: "border-emerald-200", dot: "bg-emerald-500" },
};

export default function StaffMessaging({ defaultRecipientType = "patient" }) {
  const { user } = useAuth();
  const { t } = useLanguage();
  const { on, send } = useWebSocket();
  const location = useLocation();

  const [threads, setThreads] = useState([]);
  const [selectedThread, setSelectedThread] = useState(null);
  const [messages, setMessages] = useState([]);
  const [hasMore, setHasMore] = useState(false);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [newMsg, setNewMsg] = useState("");
  const [sending, setSending] = useState(false);
  const [patientContext, setPatientContext] = useState(null);
  const [showQuickReplies, setShowQuickReplies] = useState(false);
  const [peerTyping, setPeerTyping] = useState(false);
  const [uploading, setUploading] = useState(false);

  // New conversation modal
  const [showNewModal, setShowNewModal] = useState(false);
  const [recipients, setRecipients] = useState({ clinicians: [], patients: [] });
  const [recipientType, setRecipientType] = useState(defaultRecipientType);
  const [selectedRecipient, setSelectedRecipient] = useState(null);
  const [linkedPatient, setLinkedPatient] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [creating, setCreating] = useState(false);

  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const typingTimeoutRef = useRef(null);
  const fileInputRef = useRef(null);

  // Thread list search
  const [threadSearch, setThreadSearch] = useState("");

  // ---- Data fetching ----

  useEffect(() => {
    fetchThreads();
    fetchRecipients();
    if (location.state?.patientId) {
      setShowNewModal(true);
      setRecipientType("patient");
    }
  }, []);

  useEffect(() => {
    if (!on) return;
    const unsub1 = on("NEW_MESSAGE", (data) => {
      if (selectedThread && data.threadId === selectedThread.id) {
        setMessages(prev => [...prev, data.message]);
        markRead(selectedThread.id);
        scrollToBottom();
      } else {
        fetchThreads();
        toast.info("Nouveau message");
      }
    });
    const unsub2 = on("TYPING", (data) => {
      if (selectedThread && data.threadId === selectedThread.id && data.userId !== user?.id) {
        setPeerTyping(true);
        clearTimeout(typingTimeoutRef.current);
        typingTimeoutRef.current = setTimeout(() => setPeerTyping(false), 3000);
      }
    });
    return () => { unsub1(); unsub2(); };
  }, [on, selectedThread]);

  const fetchThreads = async () => {
    try {
      const res = await api.get("/threads");
      const list = getItems(res.data);
      // Sort: unread first, then by date
      list.sort((a, b) => {
        if (a.unreadCount > 0 && b.unreadCount === 0) return -1;
        if (a.unreadCount === 0 && b.unreadCount > 0) return 1;
        return new Date(b.lastMessageAt) - new Date(a.lastMessageAt);
      });
      setThreads(list);
    } catch {
      toast.error("Erreur lors du chargement");
    }
  };

  const fetchRecipients = async () => {
    try {
      const res = await api.get("/threads/recipients");
      setRecipients(res.data);
    } catch {}
  };

  const handleDeleteThread = async (thread) => {
    const isSuperAdmin = user?.role === "SUPER_ADMIN_SAAS";
    const scopeLabel = isSuperAdmin
      ? "Supprimer cette conversation ?\n\nOK = supprimer pour tout le monde (messages wipes)\nAnnuler = garder"
      : "Retirer cette conversation de ta liste ?\n(Les autres participants la conservent.)";
    const ok = window.confirm(scopeLabel);
    if (!ok) return;
    const scope = isSuperAdmin ? "all" : "me";
    try {
      await api.delete(`/threads/${thread.id}?scope=${scope}`);
      toast.success(scope === "all" ? "Conversation supprimee pour tous" : "Conversation retiree de votre liste");
      if (selectedThread?.id === thread.id) {
        setSelectedThread(null);
        setMessages([]);
      }
      fetchThreads();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de la suppression");
    }
  };

  const selectThread = async (thread) => {
    setSelectedThread(thread);
    setPatientContext(null);
    setPeerTyping(false);
    try {
      const res = await api.get(`/threads/${thread.id}/messages`);
      const data = res.data;
      setMessages(data.messages || data);
      setHasMore(data.hasMore || false);
      await markRead(thread.id);
      fetchThreads();
      setTimeout(scrollToBottom, 100);
      // Fetch patient context
      try {
        const ctx = await api.get(`/threads/${thread.id}/patient-context`);
        if (ctx.data.hasPatient) setPatientContext(ctx.data);
      } catch {}
    } catch {
      toast.error("Erreur");
    }
  };

  const loadOlderMessages = async () => {
    if (!hasMore || loadingOlder || messages.length === 0) return;
    setLoadingOlder(true);
    const container = messagesContainerRef.current;
    const oldHeight = container?.scrollHeight || 0;
    try {
      const firstId = messages[0]?.id;
      const res = await api.get(`/threads/${selectedThread.id}/messages?before=${firstId}&limit=50`);
      const data = res.data;
      const older = data.messages || [];
      if (older.length > 0) {
        setMessages(prev => [...older, ...prev]);
        setHasMore(data.hasMore || false);
        // Restore scroll position
        requestAnimationFrame(() => {
          if (container) container.scrollTop = container.scrollHeight - oldHeight;
        });
      } else {
        setHasMore(false);
      }
    } catch {} finally {
      setLoadingOlder(false);
    }
  };

  const markRead = async (threadId) => {
    try { await api.patch(`/threads/${threadId}/read`); } catch {}
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  // ---- Scroll detection for loading older ----
  const handleScroll = useCallback(() => {
    const container = messagesContainerRef.current;
    if (container && container.scrollTop < 60 && hasMore && !loadingOlder) {
      loadOlderMessages();
    }
  }, [hasMore, loadingOlder, messages]);

  // ---- Typing indicator ----
  const handleInputChange = (e) => {
    setNewMsg(e.target.value);
    if (send && selectedThread) {
      send({ type: "TYPING", threadId: selectedThread.id, userId: user?.id });
    }
  };

  // ---- Send message ----
  const sendMessage = async (e) => {
    e.preventDefault();
    if (!newMsg.trim() || !selectedThread) return;
    setSending(true);
    try {
      const res = await api.post(`/threads/${selectedThread.id}/messages`, { content: newMsg });
      setMessages(prev => [...prev, res.data]);
      setNewMsg("");
      setShowQuickReplies(false);
      setTimeout(scrollToBottom, 100);
    } catch {
      toast.error("Erreur d'envoi");
    } finally {
      setSending(false);
    }
  };

  const sendQuickReply = (text) => {
    setNewMsg(text);
    setShowQuickReplies(false);
  };

  // ---- File upload ----
  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !selectedThread) return;
    if (file.size > 10 * 1024 * 1024) {
      toast.error("Fichier trop volumineux (max 10 Mo)");
      return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await api.post(`/threads/${selectedThread.id}/messages/file`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setMessages(prev => [...prev, res.data]);
      setTimeout(scrollToBottom, 100);
      toast.success("Fichier envoye");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur d'envoi");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  // ---- New thread ----
  const createNewThread = async () => {
    if (!selectedRecipient) { toast.error("Veuillez selectionner un destinataire"); return; }
    setCreating(true);
    try {
      const payload = recipientType === "patient"
        ? { patientId: selectedRecipient.id }
        : { recipientId: selectedRecipient.id, linkedPatientId: linkedPatient?.id || null };
      const res = await api.post("/threads", payload);
      setShowNewModal(false);
      setSelectedRecipient(null);
      setLinkedPatient(null);
      setSearchQuery("");
      fetchThreads();
      selectThread(res.data);
      toast.success("Conversation creee");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally {
      setCreating(false);
    }
  };

  const filteredRecipients = recipientType === "patient"
    ? recipients.patients.filter(p => p.name.toLowerCase().includes(searchQuery.toLowerCase()))
    : defaultRecipientType === "clinician"
      ? recipients.clinicians.filter(c => c.role === "IDE" && c.name.toLowerCase().includes(searchQuery.toLowerCase()))
      : recipients.clinicians.filter(c => c.name.toLowerCase().includes(searchQuery.toLowerCase()));

  // ---- Render ----

  // Thread display helpers
  const getThreadDisplayName = (thread) => {
    if (thread.threadType === "CLINICIAN") {
      return thread.participantNames?.filter(n => n).join(", ") || "Équipe soignante";
    }
    return thread.patientName || "Patient";
  };
  const getInitials = (name) => {
    if (!name) return "?";
    const parts = name.trim().split(/\s+/);
    return (parts[0][0] || "") + (parts[1]?.[0] || "");
  };
  const STATUS_DOT = {
    CRITICAL: "bg-rose-500",
    HOSPITALIZED: "bg-purple-500",
    WATCH: "bg-amber-500",
    STABLE: "bg-emerald-500",
  };

  const filteredThreads = threadSearch.trim()
    ? threads.filter(t => getThreadDisplayName(t).toLowerCase().includes(threadSearch.toLowerCase()))
    : threads;

  return (
    <ProfessionalLayout title={t("messages.title")}>
      <MessagingNoticeModal />
      <div className="flex gap-4 h-[calc(100vh-9rem)] lg:h-[calc(100vh-10rem)]">
        {/* Thread List - mobile: full-width when no thread / hidden when thread selected; desktop: fixed 320px */}
        <div
          className={`
            ${selectedThread ? "hidden lg:flex" : "flex"}
            w-full lg:w-80 flex-shrink-0 bg-white rounded-2xl border border-slate-200/60 overflow-hidden flex-col
          `}
          data-testid="thread-list"
        >
          <div className="p-4 border-b border-slate-100 flex items-center justify-between gap-2">
            <h3 className="font-semibold text-slate-800 text-sm">Conversations</h3>
            <button
              onClick={() => setShowNewModal(true)}
              data-testid="new-message-btn"
              className="p-2 bg-emerald-50 text-emerald-600 rounded-xl hover:bg-emerald-100 transition-all duration-200"
              title="Nouvelle conversation"
            >
              <Plus className="w-4 h-4" />
            </button>
          </div>

          {/* Search */}
          <div className="px-3 pt-3 pb-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="text"
                value={threadSearch}
                onChange={e => setThreadSearch(e.target.value)}
                placeholder="Rechercher..."
                data-testid="thread-search"
                className="w-full pl-9 pr-3 py-2 bg-slate-50 border border-transparent focus:bg-white focus:border-emerald-300 rounded-lg text-sm outline-none"
              />
            </div>
          </div>

          <div className="flex-1 overflow-y-auto">
            {filteredThreads.length === 0 ? (
              <div className="p-8 text-center text-slate-400 text-sm">
                {threadSearch ? "Aucun résultat" : "Aucune conversation"}
              </div>
            ) : filteredThreads.map(thread => {
              const displayName = getThreadDisplayName(thread);
              const isPatient = thread.threadType !== "CLINICIAN";
              const initials = getInitials(displayName);
              const statusDot = isPatient && thread.patientStatus ? STATUS_DOT[thread.patientStatus] : null;
              return (
                <div
                  key={thread.id}
                  className={`group relative border-b border-slate-100/80 transition-all duration-150 ${
                    selectedThread?.id === thread.id
                      ? "bg-emerald-50/80 border-l-2 border-l-emerald-500"
                      : thread.unreadCount > 0
                        ? "bg-sky-50/40 hover:bg-sky-50/70"
                        : "hover:bg-slate-50"
                  }`}
                >
                  <button
                    onClick={() => selectThread(thread)}
                    data-testid={`thread-${thread.id}`}
                    className="w-full p-3 text-left pr-10 flex items-start gap-3 min-h-[68px]"
                  >
                    {/* Avatar with status dot */}
                    <div className="relative flex-shrink-0">
                      <div className={`w-11 h-11 rounded-full flex items-center justify-center font-semibold text-sm ${
                        isPatient ? "bg-emerald-100 text-emerald-700" : "bg-sky-100 text-sky-700"
                      }`}>
                        {initials.toUpperCase()}
                      </div>
                      {statusDot && (
                        <span className={`absolute bottom-0 right-0 w-3 h-3 rounded-full border-2 border-white ${statusDot}`} title={thread.patientStatus} />
                      )}
                      {!isPatient && (
                        <span className="absolute -bottom-0.5 -right-0.5 w-5 h-5 rounded-full bg-sky-500 border-2 border-white flex items-center justify-center">
                          <Stethoscope className="w-2.5 h-2.5 text-white" strokeWidth={3} />
                        </span>
                      )}
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <span className={`text-sm truncate ${thread.unreadCount > 0 ? "font-bold text-slate-900" : "font-medium text-slate-700"}`}>
                          {displayName}
                        </span>
                        {thread.lastMessageAt && (
                          <span className="text-[10px] text-slate-400 flex-shrink-0">
                            {new Date(thread.lastMessageAt).toLocaleDateString("fr-FR", { day: "numeric", month: "short" })}
                          </span>
                        )}
                      </div>
                      {/* Chips */}
                      <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                        {isPatient && thread.patientPathology && (
                          <span className="text-[10px] px-1.5 py-0.5 bg-slate-100 text-slate-600 rounded truncate max-w-[120px]">
                            {thread.patientPathology}
                          </span>
                        )}
                        {thread.linkedPatientName && (
                          <span className="text-[10px] px-1.5 py-0.5 bg-sky-50 text-sky-700 rounded truncate">
                            {thread.linkedPatientName}
                          </span>
                        )}
                      </div>
                      {/* Last message preview */}
                      {thread.lastMessagePreview ? (
                        <p className={`text-xs mt-1 truncate ${thread.unreadCount > 0 ? "text-slate-700 font-medium" : "text-slate-400"}`}>
                          {thread.lastMessageSender ? `${thread.lastMessageSender.split(" ")[0]} : ` : ""}
                          {thread.lastMessagePreview}
                        </p>
                      ) : (
                        <p className="text-xs mt-1 text-slate-300 italic">Pas encore de message</p>
                      )}
                    </div>

                    {/* Unread badge */}
                    {thread.unreadCount > 0 && (
                      <span className="bg-emerald-500 text-white text-[10px] min-w-[20px] h-[20px] px-1.5 rounded-full flex items-center justify-center font-bold flex-shrink-0 self-center">
                        {thread.unreadCount}
                      </span>
                    )}
                  </button>
                  <button
                    onClick={(e) => { e.stopPropagation(); handleDeleteThread(thread); }}
                    data-testid={`delete-thread-${thread.id}`}
                    className="absolute top-3 right-2 p-1.5 text-slate-300 hover:text-red-600 hover:bg-red-50 rounded-md opacity-0 group-hover:opacity-100 lg:group-hover:opacity-100 transition-opacity"
                    title="Supprimer la conversation"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              );
            })}
          </div>
        </div>

        {/* Messages Area */}
        <div className={`
          ${selectedThread ? "flex" : "hidden lg:flex"}
          flex-1 flex-col bg-white rounded-2xl border border-slate-200/60 overflow-hidden
        `}>
          {!selectedThread ? (
            <div className="flex-1 flex items-center justify-center text-slate-400">
              <div className="text-center">
                <MessageSquare className="w-12 h-12 mx-auto mb-3 opacity-20" strokeWidth={1} />
                <p className="text-sm">Sélectionnez une conversation</p>
                <button onClick={() => setShowNewModal(true)} className="mt-4 px-4 py-2 bg-emerald-50 text-emerald-600 rounded-xl text-sm font-medium hover:bg-emerald-100 transition-colors">
                  Nouvelle conversation
                </button>
              </div>
            </div>
          ) : (
            <>
              {/* Thread Header + Patient Context */}
              <div className="border-b border-slate-100">
                <div className="p-3 lg:p-4 flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2 min-w-0">
                    {/* Mobile back button */}
                    <button
                      onClick={() => setSelectedThread(null)}
                      data-testid="back-to-threads"
                      className="lg:hidden p-2 -ml-2 rounded-lg hover:bg-slate-100 min-w-[44px] min-h-[44px] flex items-center justify-center flex-shrink-0"
                      aria-label="Retour"
                    >
                      <ChevronLeft className="w-5 h-5 text-slate-600" />
                    </button>
                    {selectedThread.threadType === "CLINICIAN"
                      ? <Stethoscope className="w-5 h-5 text-sky-500 flex-shrink-0" strokeWidth={1.5} />
                      : <User className="w-5 h-5 text-emerald-500 flex-shrink-0" strokeWidth={1.5} />
                    }
                    <h3 className="font-semibold text-slate-800 truncate">
                      {selectedThread.threadType === "CLINICIAN"
                        ? selectedThread.participantNames?.join(", ") || "Clinicien"
                        : selectedThread.patientName || "Patient"}
                    </h3>
                  </div>
                </div>
                {/* Mini patient card */}
                {patientContext && (
                  <div className={`mx-4 mb-3 px-3.5 py-2.5 rounded-xl border flex items-center gap-4 text-xs ${STATUS_COLORS[patientContext.status]?.bg || "bg-slate-50"} ${STATUS_COLORS[patientContext.status]?.border || "border-slate-200"}`} data-testid="patient-context-card">
                    <div className="flex items-center gap-1.5">
                      <span className={`w-2 h-2 rounded-full ${STATUS_COLORS[patientContext.status]?.dot || "bg-slate-400"}`} />
                      <span className={`font-semibold ${STATUS_COLORS[patientContext.status]?.text || "text-slate-700"}`}>
                        {patientContext.status}
                      </span>
                    </div>
                    {patientContext.pathology && (
                      <span className="text-slate-500">{patientContext.pathology}</span>
                    )}
                    {patientContext.lastCheckinAt && (
                      <span className="flex items-center gap-1 text-slate-500">
                        <Activity className="w-3 h-3" strokeWidth={1.5} />
                        Dernier check-in : {new Date(patientContext.lastCheckinAt).toLocaleDateString("fr-FR")}
                      </span>
                    )}
                    {patientContext.activeAlerts > 0 && (
                      <span className="flex items-center gap-1 text-rose-600 font-semibold">
                        <AlertTriangle className="w-3 h-3" strokeWidth={2} />
                        {patientContext.activeAlerts} alerte{patientContext.activeAlerts > 1 ? "s" : ""}
                      </span>
                    )}
                  </div>
                )}
              </div>

              {/* Messages with scroll-back */}
              <div
                ref={messagesContainerRef}
                onScroll={handleScroll}
                className="flex-1 overflow-y-auto p-4 space-y-1"
                data-testid="messages-container"
              >
                {/* Load more button */}
                {hasMore && (
                  <button
                    onClick={loadOlderMessages}
                    disabled={loadingOlder}
                    className="w-full py-2 text-xs text-slate-400 hover:text-emerald-600 flex items-center justify-center gap-1 transition-colors"
                    data-testid="load-older-btn"
                  >
                    {loadingOlder ? (
                      <div className="w-4 h-4 border-2 border-slate-300 border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <><ChevronUp className="w-3.5 h-3.5" /> Charger les messages precedents</>
                    )}
                  </button>
                )}

                {messages.map((msg, idx) => {
                  const isMe = msg.senderId === user?.id;
                  const showDate = shouldShowDateSeparator(messages, idx);
                  return (
                    <div key={msg.id}>
                      {showDate && (
                        <div className="flex items-center gap-3 my-4" data-testid="date-separator">
                          <div className="flex-1 h-px bg-slate-200" />
                          <span className="text-[11px] text-slate-400 font-medium px-2">{getDateLabel(msg.createdAt)}</span>
                          <div className="flex-1 h-px bg-slate-200" />
                        </div>
                      )}
                      <div className={`flex ${isMe ? "justify-end" : "justify-start"} mb-1`}>
                        <div className={`max-w-[70%] flex flex-col gap-0.5 ${isMe ? "items-end" : "items-start"}`}>
                          {!isMe && (
                            <span className="text-[11px] text-slate-400 px-2 font-medium">{msg.senderName}</span>
                          )}
                          <div className={`rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${
                            isMe
                              ? "bg-emerald-600 text-white rounded-br-md"
                              : "bg-slate-100 text-slate-800 rounded-bl-md"
                          }`}>
                            {msg.type === "file" && msg.file ? (
                              isImageFile(msg.file) ? (
                                <div className="space-y-2 w-full max-w-[17rem] sm:max-w-sm">
                                  <a
                                    href={getMessageFileUrl(msg.file)}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    data-testid={`message-image-preview-${msg.id}`}
                                    className="block aspect-[4/3] max-h-72 min-h-40 overflow-hidden rounded-xl bg-white/20 ring-1 ring-black/5"
                                    title="Voir la photo"
                                  >
                                    <img
                                      src={getMessageFileUrl(msg.file)}
                                      alt={msg.file.name || "Photo envoyée dans la conversation"}
                                      className="h-full w-full object-contain"
                                      loading="lazy"
                                    />
                                  </a>
                                  {msg.content && msg.content !== msg.file.name && (
                                    <p className="text-sm leading-relaxed" data-testid={`message-file-caption-${msg.id}`}>
                                      {msg.content}
                                    </p>
                                  )}
                                  <div className={`flex items-center justify-between gap-3 text-xs ${isMe ? "text-white/80" : "text-slate-500"}`}>
                                    <span className="truncate">{msg.file.name}</span>
                                    <a
                                      href={getMessageFileUrl(msg.file)}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className={`p-1.5 rounded-lg flex-shrink-0 ${isMe ? "hover:bg-white/20" : "hover:bg-slate-200"} transition-colors`}
                                      data-testid={`download-file-${msg.id}`}
                                      title="Télécharger"
                                    >
                                      <Download className="w-4 h-4" strokeWidth={1.5} />
                                    </a>
                                  </div>
                                </div>
                              ) : (
                                <div className="flex items-center gap-2.5">
                                  <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${isMe ? "bg-white/20" : "bg-slate-200"}`}>
                                    <FileText className="w-4 h-4" strokeWidth={1.5} />
                                  </div>
                                  <div className="min-w-0 flex-1">
                                    <p className="text-sm font-medium truncate">{msg.file.name}</p>
                                    <p className={`text-xs ${isMe ? "text-white/70" : "text-slate-400"}`}>
                                      {(msg.file.size / 1024).toFixed(0)} Ko
                                    </p>
                                  </div>
                                  <a
                                    href={getMessageFileUrl(msg.file)}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className={`p-1.5 rounded-lg flex-shrink-0 ${isMe ? "hover:bg-white/20" : "hover:bg-slate-200"} transition-colors`}
                                    data-testid={`download-file-${msg.id}`}
                                  >
                                    <Download className="w-4 h-4" strokeWidth={1.5} />
                                  </a>
                                </div>
                              )
                            ) : (
                              msg.content
                            )}
                          </div>
                          <span className="text-[10px] text-slate-400 px-2">
                            {new Date(msg.createdAt).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })}

                {/* Typing indicator */}
                {peerTyping && (
                  <div className="flex justify-start mb-1">
                    <div className="bg-slate-100 rounded-2xl rounded-bl-md px-4 py-3 flex gap-1">
                      <span className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
                      <span className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
                      <span className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
                    </div>
                  </div>
                )}
                <div ref={messagesEndRef} />
              </div>

              {/* Quick Replies */}
              {showQuickReplies && (
                <div className="px-4 pb-2 flex flex-wrap gap-1.5" data-testid="quick-replies">
                  {QUICK_REPLIES.map((text, i) => (
                    <button
                      key={i}
                      onClick={() => sendQuickReply(text)}
                      className="px-3 py-1.5 bg-emerald-50 text-emerald-700 text-xs rounded-full hover:bg-emerald-100 transition-colors border border-emerald-200/60"
                    >
                      {text}
                    </button>
                  ))}
                </div>
              )}

              {/* Input */}
              <form onSubmit={sendMessage} className="p-3.5 border-t border-slate-100 flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setShowQuickReplies(!showQuickReplies)}
                  className={`p-2.5 rounded-xl transition-all duration-200 ${showQuickReplies ? "bg-emerald-100 text-emerald-700" : "text-slate-400 hover:bg-slate-100 hover:text-slate-600"}`}
                  title="Reponses rapides"
                  data-testid="quick-reply-toggle"
                >
                  <Zap className="w-4 h-4" strokeWidth={1.5} />
                </button>
                <input type="file" ref={fileInputRef} onChange={handleFileSelect} className="hidden" accept="image/*,.pdf,.doc,.docx,.xls,.xlsx" data-testid="file-input-hidden" />
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading || !selectedThread}
                  className="p-2.5 text-slate-400 hover:bg-slate-100 hover:text-slate-600 rounded-xl transition-all duration-200 disabled:opacity-40"
                  title="Envoyer un fichier"
                  data-testid="attach-file-btn"
                >
                  {uploading ? (
                    <div className="w-4 h-4 border-2 border-slate-400 border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <Paperclip className="w-4 h-4" strokeWidth={1.5} />
                  )}
                </button>
                <input
                  type="text"
                  value={newMsg}
                  onChange={handleInputChange}
                  data-testid="message-input"
                  placeholder={t("messages.placeholder")}
                  className="flex-1 h-11 px-4 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-200/80 transition-shadow"
                />
                <button
                  type="submit"
                  disabled={sending || !newMsg.trim()}
                  data-testid="send-message-btn"
                  className="h-11 w-11 bg-emerald-600 text-white rounded-xl flex items-center justify-center hover:bg-emerald-700 disabled:opacity-40 transition-all duration-200 shadow-sm hover:shadow"
                >
                  <Send className="w-4 h-4" />
                </button>
              </form>
            </>
          )}
        </div>
      </div>

      {/* New Conversation Modal */}
      {showNewModal && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md animate-scale-in" data-testid="new-conversation-modal">
            <div className="p-5 border-b border-slate-100">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold text-slate-800 flex items-center gap-2">
                  <MessageSquare className="w-5 h-5 text-emerald-600" strokeWidth={1.5} />
                  Nouvelle conversation
                </h2>
                <button onClick={() => setShowNewModal(false)} className="p-2 hover:bg-slate-100 rounded-xl transition-colors">
                  <X className="w-5 h-5 text-slate-400" />
                </button>
              </div>
            </div>
            <div className="p-5 space-y-4">
              {/* Tabs */}
              <div className="flex gap-2">
                <button
                  onClick={() => { setRecipientType("patient"); setSelectedRecipient(null); }}
                  className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 ${
                    recipientType === "patient" ? "bg-emerald-600 text-white shadow-sm" : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                  }`}
                >
                  <User className="w-4 h-4" /> Patient
                </button>
                <button
                  onClick={() => { setRecipientType("clinician"); setSelectedRecipient(null); }}
                  className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 ${
                    recipientType === "clinician" ? "bg-sky-600 text-white shadow-sm" : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                  }`}
                >
                  <Stethoscope className="w-4 h-4" /> {defaultRecipientType === "clinician" ? "IDE" : "Clinicien"}
                </button>
              </div>
              {/* Search */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                  type="text" value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
                  placeholder={`Rechercher un ${recipientType === "patient" ? "patient" : "clinicien"}...`}
                  className="w-full pl-10 pr-4 py-2.5 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-emerald-200/80 focus:outline-none"
                  data-testid="recipient-search"
                />
              </div>
              {/* List */}
              <div className="max-h-48 overflow-y-auto border border-slate-200 rounded-xl">
                {filteredRecipients.length === 0 ? (
                  <div className="p-4 text-center text-slate-400 text-sm">Aucun resultat</div>
                ) : filteredRecipients.map(r => (
                  <button
                    key={r.id} onClick={() => setSelectedRecipient(r)} data-testid={`recipient-${r.id}`}
                    className={`w-full p-3 text-left hover:bg-slate-50 border-b border-slate-100 last:border-b-0 flex items-center gap-3 transition-colors ${
                      selectedRecipient?.id === r.id ? "bg-emerald-50" : ""
                    }`}
                  >
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center ${recipientType === "patient" ? "bg-emerald-100" : "bg-sky-100"}`}>
                      {recipientType === "patient" ? <User className="w-4 h-4 text-emerald-600" /> : <Stethoscope className="w-4 h-4 text-sky-600" />}
                    </div>
                    <div>
                      <p className="font-medium text-sm text-slate-800">{r.name}</p>
                      {r.pathology && <p className="text-xs text-slate-500">{r.pathology}</p>}
                      {r.role && <p className="text-xs text-slate-500">{r.role === "MEDECIN" ? "Medecin" : "IDE"}</p>}
                    </div>
                  </button>
                ))}
              </div>
              {/* Linked patient */}
              {recipientType === "clinician" && selectedRecipient && (
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">Associer a un patient (optionnel)</label>
                  <select value={linkedPatient?.id || ""} onChange={e => { const p = recipients.patients.find(p => p.id === e.target.value); setLinkedPatient(p || null); }}
                    className="w-full px-3 py-2 border border-slate-200 rounded-xl text-sm" data-testid="linked-patient-select"
                  >
                    <option value="">Aucun patient</option>
                    {recipients.patients.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                  </select>
                </div>
              )}
              {/* Actions */}
              <div className="flex gap-3 pt-1">
                <button onClick={() => setShowNewModal(false)} className="flex-1 py-2.5 border border-slate-200 rounded-xl text-slate-600 font-medium hover:bg-slate-50 transition-colors">Annuler</button>
                <button onClick={createNewThread} disabled={!selectedRecipient || creating} className="flex-1 py-2.5 bg-emerald-600 text-white rounded-xl font-medium hover:bg-emerald-700 disabled:opacity-40 transition-all" data-testid="create-conversation-btn">
                  {creating ? "..." : "Creer"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </ProfessionalLayout>
  );
}
